import {
  AudioLines,
  Check,
  Ear,
  type LucideIcon,
  Smartphone,
  Timer,
  TriangleAlert,
  Volume1,
  Volume2,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/utils/cn'
import { MICRO_LABEL, TEXT_CAPTION, TEXT_TITLE } from '../recipes'
import { liveCaption, type VoiceLiveLine } from './voice-format'
import { type VoiceTopic, voiceTopicAt } from './voice-rules'
import type { VoiceReadout, VoiceRoom } from './voice-types'

const COPY = 'humint.voice.sample'
const WARNING = 'text-warning-foreground'

/** Each line of the caption: its icon, its tone and its words. The tone never says it alone. */
const CAPTION: Record<VoiceLiveLine, { Icon: LucideIcon; tone: string; words: string }> = {
  speaking: { Icon: AudioLines, tone: 'text-primary-strong', words: 'live.speaking' },
  waiting: { Icon: Ear, tone: 'text-muted-foreground', words: 'live.waiting' },
  time: { Icon: Timer, tone: WARNING, words: 'live.time' },
  faint: { Icon: Volume1, tone: WARNING, words: 'live.faint' },
  clipping: { Icon: Volume2, tone: WARNING, words: 'live.clipping' },
  quiet: { Icon: Check, tone: 'text-foreground', words: 'room.said.quiet' },
  noisy: { Icon: TriangleAlert, tone: WARNING, words: 'room.said.noisy' },
  loud: { Icon: TriangleAlert, tone: WARNING, words: 'room.said.loud' },
}

/** One line under the ring: an icon and what it means, centred, on two lines where one will not hold it. */
export const CAPTION_LINE = 'flex items-center gap-2 text-center text-sm font-semibold text-balance'

/**
 * What the ring is doing, in words: a voice is counted, nobody speaks, or — where the meter gives
 * no level — time is counted. Where the level asks something of the subject it says that first,
 * in the warning tone: closer or louder, or further from the phone. For the first seconds, until
 * a voice is heard, it says what the room check found. The line follows the voice at once, so it
 * is for the eye only: a screen reader is told through the instrument's own status line, which
 * says a silence only once it has lasted and the same request not again for a while.
 */
function LiveCaption({ readout, room }: { readout: VoiceReadout; room: VoiceRoom | null }) {
  const { t } = useTranslation()
  const { Icon, tone, words } = CAPTION[liveCaption(readout, room)]
  return (
    <p aria-hidden className={cn(CAPTION_LINE, tone)}>
      <Icon className="size-4 shrink-0" />
      {t(`${COPY}.${words}`)}
    </p>
  )
}

/**
 * What the subject is asked to talk about, one topic at a time. A new topic comes up from below;
 * it is not announced — it changes every few seconds, and the operator reads it when needed.
 */
export function TopicLine({ topic }: { topic: VoiceTopic }) {
  const { t } = useTranslation()
  return (
    <p className="flex min-h-14 flex-col items-center gap-1 text-center">
      <span className={MICRO_LABEL}>{t('humint.voice.sample.topics.prompt')}</span>
      <span
        key={topic}
        className={cn(
          TEXT_TITLE,
          'font-medium text-balance animate-in fade-in-0 slide-in-from-bottom-1 duration-300 motion-reduce:animate-none'
        )}
      >
        {t(`humint.voice.sample.topics.${topic}`)}
      </span>
    </p>
  )
}

/**
 * Whether the screen stays awake while it records: the last line of the instrument, under what
 * the operator reads all the time. On a short sheet it is the line that gives way.
 */
export function WakeLockNote({ wakeLock }: { wakeLock: 'held' | 'unsupported' }) {
  const { t } = useTranslation()
  const held = wakeLock === 'held'
  return (
    <p
      className={cn(
        TEXT_CAPTION,
        'flex max-w-[40ch] gap-2',
        held ? 'text-muted-foreground' : 'text-warning-foreground'
      )}
    >
      <Smartphone aria-hidden className="mt-px size-4 shrink-0" />
      {held
        ? t('humint.questioning.recording.wakeLock')
        : t('humint.questioning.recording.wakeLockUnsupported')}
    </p>
  )
}

export interface VoiceSampleLiveProps {
  readout: VoiceReadout
  wakeLock: 'held' | 'unsupported'
  /** What the room check found; null or absent where it could not tell, or none was made. */
  room?: VoiceRoom | null
}

/**
 * Under the ring while it records: what it hears — or what to ask of the voice — what to talk
 * about now, and the screen. The topics turn from the moment the speech is counted, not from the
 * room check before it.
 */
export function VoiceSampleLive({ readout, wakeLock, room = null }: VoiceSampleLiveProps) {
  return (
    <div className="flex w-full flex-col items-center gap-4">
      <LiveCaption readout={readout} room={room} />
      <TopicLine topic={voiceTopicAt(readout.talkMs)} />
      <WakeLockNote wakeLock={wakeLock} />
    </div>
  )
}
